import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { trackPhoneCall, trackQuoteRequest } from "@/utils/gtag";

const services = [
  "에폭시 바닥공사",
  "우레탄 방수",
  "외벽도장",
  "내부도장",
  "실내건축",
  "석공사",
  "기타 / 복합공사",
];

const ADDRESS = "부산시 기장군 장안읍 좌천로 20, 101호";

const MAP_EMBED =
  "https://www.google.com/maps?q=%EB%B6%80%EC%82%B0%EC%8B%9C+%EA%B8%B0%EC%9E%A5%EA%B5%B0+%EC%9E%A5%EC%95%88%EC%9D%8D+%EC%A2%8C%EC%B2%9C%EB%A1%9C+20&output=embed";

type Status = "idle" | "loading" | "success" | "error";

const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "무료 현장진단 및 견적문의 | 제이아이앤씨 JI&C",
  "description": "제이아이앤씨 무료 현장진단 및 견적문의. 현장 정보를 남겨주시면 24시간 이내에 방문 일정과 견적을 회신드립니다.",
  "url": import.meta.env.VITE_SITE_URL + "/contact",
  "isPartOf": {
    "@type": "WebSite",
    "name": "제이아이앤씨 JI&C",
    "url": import.meta.env.VITE_SITE_URL
  }
};

export default function ContactPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string>("");
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);

    const hp = (fd.get("website_alt") as string) || "";
    if (hp.trim() !== "") {
      setStatus("success");
      form.reset();
      setPrivacyAgreed(false);
      return;
    }
    fd.delete("website_alt");

    const name = (fd.get("name") as string || "").trim();
    const phone = (fd.get("phone") as string || "").trim();
    const message = (fd.get("message") as string || "").trim();

    if (!name || !phone) {
      setFormError("성함과 연락처는 필수 입력 항목입니다.");
      setStatus("error");
      return;
    }
    if (message.length > 500) {
      setFormError("문의 내용은 500자를 초과할 수 없습니다.");
      setStatus("error");
      return;
    }
    if (!privacyAgreed) {
      setFormError("개인정보 수집·이용에 동의해주세요.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => body.append(key, value.toString()));

      const response = await fetch("https://readdy.ai/api/form/d9jirqcoq4dpg2lrnkfg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const containsSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !containsSpam) {
        trackQuoteRequest();
        setStatus("success");
        form.reset();
        setPrivacyAgreed(false);
      } else {
        setStatus("error");
        setFormError(serverMsg || "전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      }
    } catch {
      setStatus("error");
      setFormError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <>
      <JsonLd data={contactPageSchema} />
      <div className="w-full bg-white text-navy-900">
        <Navbar />
        <main>
          {/* Hero */}
          <section className="relative w-full bg-navy-900 pt-24 pb-14 md:pt-36 md:pb-28">
            <div className="w-full px-5 md:px-12 lg:px-16">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <span className="w-8 h-px bg-point-500"></span>
                  <span className="text-point-400 text-xs font-semibold uppercase tracking-[0.3em]">
                    Get In Touch
                  </span>
                </div>
                <h1 className="font-hero text-white text-3xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6">
                  무료 현장진단 &amp; 견적문의
                </h1>
                <p className="text-white/60 text-sm md:text-lg leading-relaxed max-w-2xl">
                  현장 정보를 남겨주시면 담당자가 24시간 이내에 방문 일정과 개략 견적을 회신드립니다.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Content */}
          <section className="w-full py-12 md:py-20 lg:py-24 bg-neutral-50">
            <div className="w-full px-5 md:px-12 lg:px-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                {/* Left - contact info + map */}
                <div className="lg:col-span-5 flex flex-col gap-4 md:gap-6">
                  {/* Quick contact cards - 2 columns on all sizes */}
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <a
                      href="tel:0517270477"
                      onClick={() => trackPhoneCall()}
                      className="group p-4 md:p-6 bg-white border border-neutral-200 rounded-2xl hover:border-point-400 transition-all cursor-pointer"
                    >
                      <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-point-50 text-point-600 mb-3 md:mb-4 group-hover:bg-point-500 group-hover:text-white transition-colors">
                        <i className="ri-phone-line text-lg md:text-xl"></i>
                      </div>
                      <div className="text-xs text-neutral-500 mb-1">전화 문의</div>
                      <div className="font-hero text-navy-900 text-base md:text-xl leading-snug">051-727-0477</div>
                      <div className="text-xs text-neutral-400 mt-1">평일 09:00-18:00</div>
                    </a>
                    <a
                      href="https://pf.kakao.com/_exaxmSn"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="group p-4 md:p-6 bg-white border border-neutral-200 rounded-2xl hover:border-point-400 transition-all cursor-pointer"
                    >
                      <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-point-50 text-point-600 mb-3 md:mb-4 group-hover:bg-point-500 group-hover:text-white transition-colors">
                        <i className="ri-kakao-talk-line text-lg md:text-xl"></i>
                      </div>
                      <div className="text-xs text-neutral-500 mb-1">카카오톡</div>
                      <div className="font-hero text-navy-900 text-base md:text-xl leading-snug">@제이아이앤씨</div>
                      <div className="text-xs text-neutral-400 mt-1">24시간 접수</div>
                    </a>
                    <a
                      href="mailto:ksh10090000@hanmail.net"
                      className="group p-4 md:p-6 bg-white border border-neutral-200 rounded-2xl hover:border-point-400 transition-all cursor-pointer"
                    >
                      <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-point-50 text-point-600 mb-3 md:mb-4 group-hover:bg-point-500 group-hover:text-white transition-colors">
                        <i className="ri-mail-line text-lg md:text-xl"></i>
                      </div>
                      <div className="text-xs text-neutral-500 mb-1">이메일</div>
                      <div className="text-navy-900 text-xs md:text-sm font-medium leading-snug break-all">
                        ksh10090000<br />@hanmail.net
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">1영업일 회신</div>
                    </a>
                    <div className="p-4 md:p-6 bg-navy-900 rounded-2xl text-white">
                      <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-point-500 text-white mb-3 md:mb-4">
                        <i className="ri-map-pin-line text-lg md:text-xl"></i>
                      </div>
                      <div className="text-xs text-white/60 mb-1">본사 주소</div>
                      <div className="text-white text-xs md:text-sm font-medium leading-snug">{ADDRESS}</div>
                    </div>
                  </div>

                  {/* Map */}
                  <div className="relative w-full h-48 md:h-72 rounded-2xl overflow-hidden border border-neutral-200 bg-white">
                    <iframe
                      title="JI&C Location"
                      src={MAP_EMBED}
                      className="absolute inset-0 w-full h-full"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                </div>

                {/* Right - form */}
                <div className="lg:col-span-7">
                  <form
                    id="jinc-contact-page-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-white border border-neutral-200 rounded-2xl p-5 sm:p-7 md:p-10"
                  >
                    <h2 className="font-hero text-navy-900 text-xl md:text-2xl mb-5 md:mb-6">
                      견적 신청서
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          성함 <span className="text-point-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="홍길동"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          연락처 <span className="text-point-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="010-1234-5678"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          이메일
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="you@company.com"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          소속 / 회사
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="회사명 또는 개인"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          문의 공종
                        </label>
                        <div className="relative">
                          <select
                            name="service"
                            defaultValue={services[0]}
                            className="w-full appearance-none px-4 py-3 pr-10 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors bg-white cursor-pointer"
                          >
                            {services.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-neutral-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          현장 위치
                        </label>
                        <input
                          type="text"
                          name="location"
                          placeholder="예) 부산 사상구, 울산 울주군"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          시공 면적 (평)
                        </label>
                        <input
                          type="text"
                          name="area"
                          placeholder="예) 350평 / 미정"
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-navy-900 mb-2">
                          문의 내용{" "}
                          <span className="text-neutral-400 text-xs">(최대 500자)</span>
                        </label>
                        <textarea
                          name="message"
                          rows={5}
                          maxLength={500}
                          placeholder="현장 상태, 희망 공기, 예산 규모 등 자유롭게 남겨주세요."
                          className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-navy-800 transition-colors resize-none"
                        ></textarea>
                      </div>

                      {/* Honeypot */}
                      <div className="hp-guard" aria-hidden="true">
                        <label>Website (leave blank)</label>
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                    </div>

                    {/* Privacy Agree */}
                    <div className="mt-5 md:mt-6">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacyAgreed}
                          onChange={(e) => setPrivacyAgreed(e.target.checked)}
                          className="mt-1 w-4 h-4 accent-point-500 cursor-pointer shrink-0"
                        />
                        <span className="text-xs md:text-sm text-neutral-600 leading-relaxed">
                          개인정보 수집·이용에 동의합니다. (수집항목: 성함, 연락처, 이메일 / 목적: 견적 상담 및 회신 / 보관: 3년)
                        </span>
                      </label>
                    </div>

                    {/* Status Messages */}
                    {status === "success" && (
                      <div className="mt-5 flex items-start gap-3 p-4 rounded-lg bg-point-50 border border-point-200">
                        <div className="w-5 h-5 flex items-center justify-center text-point-600 mt-0.5 shrink-0">
                          <i className="ri-checkbox-circle-line"></i>
                        </div>
                        <div className="text-sm text-point-800">
                          문의가 접수되었습니다. 담당자가 24시간 이내에 회신드리겠습니다.
                        </div>
                      </div>
                    )}

                    {status === "error" && formError && (
                      <div className="mt-5 flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
                        <div className="w-5 h-5 flex items-center justify-center text-red-600 mt-0.5 shrink-0">
                          <i className="ri-error-warning-line"></i>
                        </div>
                        <div className="text-sm text-red-700">{formError}</div>
                      </div>
                    )}

                    {/* Submit */}
                    <div className="mt-5 md:mt-8 flex flex-col gap-3">
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-point-500 hover:bg-point-600 disabled:opacity-70 text-white text-base font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {status === "loading" ? (
                          <>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin"></i>
                            </div>
                            전송 중...
                          </>
                        ) : (
                          <>
                            무료 견적 요청하기
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-send-plane-line"></i>
                            </div>
                          </>
                        )}
                      </button>
                      <p className="text-xs text-neutral-400 text-center">
                        * 문의 접수 후 24시간 이내에 회신드립니다.
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}